import { useMemo, useState } from "react";
import { Button, Empty, Input, Tag, Tree } from "antd";
import {
  BookOutlined,
  LinkOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  getEffectivePermissions,
  PAGE_DESCRIPTIONS,
  PAGE_LABELS,
} from "../config/pagePermissions";
import { GUIDE_CONTENT, GUIDE_CONTROL_DETAILS } from "../config/guideContent";
import "../style/guide.scss";

const Guide = ({ title }) => {
  document.title = title;
  const user = useSelector((state) => state.auth.user);
  const roles = useSelector((state) => state.permissions.roles);
  const [search, setSearch] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);
  const permissions = getEffectivePermissions(user, roles);

  const pages = useMemo(() => {
    const allowed = GUIDE_CONTENT.filter((page) =>
      permissions.includes(page.key),
    );
    permissions.forEach((key) => {
      if (!allowed.some((page) => page.key === key) && PAGE_DESCRIPTIONS[key]) {
        allowed.push({
          key,
          label: PAGE_LABELS[key] || key,
          overview: PAGE_DESCRIPTIONS[key],
          groups: [{ title: "Overview", items: [PAGE_DESCRIPTIONS[key]] }],
        });
      }
    });
    return allowed;
  }, [permissions]);

  const visiblePages = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return pages;
    return pages.filter((page) => {
      const controls = GUIDE_CONTROL_DETAILS[page.key];
      return [
        page.label,
        page.overview,
        ...page.groups.flatMap((group) => [group.title, ...group.items]),
        ...(controls?.buttons || []),
        ...(controls?.filters || []),
      ]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [pages, search]);

  const treeData = visiblePages.map((page) => {
    const controls = GUIDE_CONTROL_DETAILS[page.key];
    const groups = [
      ...page.groups,
      ...(controls?.buttons?.length
        ? [{ title: "Buttons and actions", items: controls.buttons }]
        : []),
      ...(controls?.filters?.length
        ? [{ title: "Search, filters, and fields", items: controls.filters }]
        : []),
    ];
    return {
      key: `page-${page.key}`,
      title: (
        <div className="guide-page-node">
          <span className="guide-page-icon">
            <BookOutlined />
          </span>
          <div>
            <strong>{page.label}</strong>
            <small>{page.overview}</small>
          </div>
          {page.route && (
            <Link to={page.route} onClick={(event) => event.stopPropagation()}>
              <LinkOutlined /> Open page
            </Link>
          )}
        </div>
      ),
      children: groups.map((group, groupIndex) => ({
        key: `page-${page.key}-group-${groupIndex}`,
        title: <strong className="guide-group-title">{group.title}</strong>,
        children: group.items.map((item, itemIndex) => ({
          key: `page-${page.key}-group-${groupIndex}-item-${itemIndex}`,
          title: <span className="guide-leaf">{item}</span>,
          isLeaf: true,
        })),
      })),
    };
  });
  const allKeys = treeData.flatMap((page) => [
    page.key,
    ...page.children.map((group) => group.key),
  ]);

  return (
    <div className="guide-page">
      <header className="guide-hero">
        <div className="guide-hero-icon">
          <BookOutlined />
        </div>
        <div>
          <Tag>ROLE-BASED GUIDE</Tag>
          <h1>Resume Builder Guide</h1>
          <p>
            Explore the pages, controls, filters, and workflows available to
            your account.
          </p>
        </div>
      </header>
      <section className="guide-toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search pages, buttons, filters, or workflows"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div>
          <Button
            icon={<PlusSquareOutlined />}
            onClick={() => setExpandedKeys(allKeys)}
          >
            Expand all
          </Button>
          <Button
            icon={<MinusSquareOutlined />}
            onClick={() => setExpandedKeys([])}
          >
            Collapse all
          </Button>
        </div>
      </section>
      <section className="guide-tree-card">
        <div className="guide-tree-summary">
          <strong>
            {visiblePages.length} page guide
            {visiblePages.length === 1 ? "" : "s"}
          </strong>
          <span>Content is limited to your role and page permissions.</span>
        </div>
        {treeData.length ? (
          <Tree
            blockNode
            showLine={{ showLeafIcon: false }}
            switcherIcon={<RightOutlined className="guide-tree-chevron" />}
            treeData={treeData}
            expandedKeys={expandedKeys}
            onExpand={setExpandedKeys}
          />
        ) : (
          <Empty description="No guide topics match your search" />
        )}
      </section>
    </div>
  );
};

export default Guide;
